import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { PUBLIC_SECTORS, PUBLIC_TEMPLATES } from '../data/publicData';
import { SectionHeading } from '../components/ui/SectionHeading';
import {
  Building2,
  Anchor,
  Scale,
  Stethoscope,
  Compass,
  Utensils,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Eye,
  ShieldCheck,
} from 'lucide-react';

export const SectorsPage: React.FC = () => {
  const { navigate } = useNavigation();

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Building2':
        return <Building2 className="w-6 h-6" />;
      case 'Anchor':
        return <Anchor className="w-6 h-6" />;
      case 'Scale':
        return <Scale className="w-6 h-6" />;
      case 'Stethoscope':
        return <Stethoscope className="w-6 h-6" />;
      case 'Compass':
        return <Compass className="w-6 h-6" />;
      case 'Utensils':
        return <Utensils className="w-6 h-6" />;
      default:
        return <Sparkles className="w-6 h-6" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          badge="Sektörel Çözümler"
          title="Her Sektörün Dinamiğine Uygun Hazır Mimari"
          subtitle="Genel şablonlarla vakit kaybetmeyin. Sektörünüzün ihtiyaç duyduğu tüm modüller, rezervasyon motorları ve kurumsal bloklar tek tıkla elinizin altında."
        />

        {/* Sectors Grid */}
        <div className="mt-16 space-y-16">
          {PUBLIC_SECTORS.map((sector, index) => {
            const isReversed = index % 2 !== 0;
            const matchingTemplate = PUBLIC_TEMPLATES.find(
              (t) => t.id === sector.recommendedTemplateId
            ) || PUBLIC_TEMPLATES[0];

            return (
              <div
                key={sector.id}
                id={`sector-${sector.id.toLowerCase()}`}
                className="p-6 sm:p-10 rounded-3xl bg-slate-900/80 border border-slate-800 shadow-2xl hover:border-slate-700 transition duration-300"
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                    isReversed ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Text Column */}
                  <div
                    className={`lg:col-span-7 space-y-6 ${
                      isReversed ? 'lg:order-2' : 'lg:order-1'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-blue-600/10 text-blue-400 border border-blue-500/20 flex items-center justify-center shadow-md">
                        {getIcon(sector.icon)}
                      </div>
                      <div>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-400 block">
                          {sector.badgeText}
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                          {sector.name}
                        </h3>
                      </div>
                    </div>

                    <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                      {sector.description}
                    </p>

                    {/* Included Modules Pills */}
                    <div>
                      <span className="text-xs font-semibold uppercase text-slate-400 tracking-wider mb-2.5 block">
                        Dahili Hazır Modüller
                      </span>
                      <div className="flex flex-wrap gap-2">
                        {sector.includedModules.map((mod, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-800 text-slate-200 border border-slate-700/80 flex items-center gap-1.5"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />
                            {mod}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Sector Key Benefits */}
                    <div className="space-y-2 pt-2">
                      {sector.keyFeatures.map((feat, i) => (
                        <div key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-2 flex-shrink-0" />
                          <span>{feat}</span>
                        </div>
                      ))}
                    </div>

                    {/* Action Bar */}
                    <div className="pt-4 flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => navigate('/kayit')}
                        className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs sm:text-sm shadow-lg shadow-blue-600/25 flex items-center gap-2 transition active:scale-95"
                      >
                        <span>Bu Sektörde Başla</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => navigate('/temalar')}
                        className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs sm:text-sm font-semibold transition"
                      >
                        İlgili Temaları İncele
                      </button>
                    </div>
                  </div>

                  {/* Image Column */}
                  <div
                    className={`lg:col-span-5 ${
                      isReversed ? 'lg:order-1' : 'lg:order-2'
                    }`}
                  >
                    <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl aspect-[4/3]">
                      <img
                        src={sector.heroImage}
                        alt={sector.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />

                      {/* Stats badge overlay */}
                      <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-slate-950/90 backdrop-blur-md border border-slate-800 flex items-center justify-between">
                        <div>
                          <span className="text-[11px] text-slate-400 block">{sector.stats.label}</span>
                          <span className="text-base font-bold text-white">{sector.stats.value}</span>
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                          Aktif Kullanımda
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-3">
            Sektörünüz listede yok mu?
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mb-6">
            Adiosoft’un esnek blok yapısı sayesinde her türlü işletme veya serbest meslek için özel web sitenizi kolayca inşa edebilirsiniz.
          </p>
          <button
            onClick={() => navigate('/kayit')}
            className="px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold shadow-lg shadow-blue-600/30 transition"
          >
            Hemen Ücretsiz Deneyin
          </button>
        </div>
      </div>
    </div>
  );
};
